import { useState, useEffect } from "react";
import { api } from "@/lib/api-client";
import { toast } from "@/components/Toaster";
import { usePermissions } from "@/hooks/usePermissions";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import type { EscalatedDocument } from "./EscalationQueue";

interface EscalationContextPanelProps {
  document: EscalatedDocument;
  onClose: () => void;
  onResolved: () => void;
}

export function EscalationContextPanel({
  document,
  onClose,
  onResolved,
}: EscalationContextPanelProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reason, setReason] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const ctx = document.escalationContext;
  const isMisidentified = ctx?.category === "misidentified_type";
  const showReclassify = isMisidentified || document.classificationSource === "fingerprint";

  const { hasPermission, checkPermission } = usePermissions();
  useEffect(() => {
    void checkPermission("delete-doc", "escalation");
  }, [checkPermission]);
  const canDelete = hasPermission("delete-doc", "escalation");

  async function handleReclassifyMisfire() {
    setIsSubmitting(true);
    try {
      await api.post(`/documents/${document.id}/reclassify-misfire`, {});
      onResolved();
    } catch {
      toast.error("Reclassify failed");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleResolve(resolution: "accept" | "override" | "reject") {
    setIsSubmitting(true);
    try {
      await api.post(`/documents/${document.id}/resolve-escalation`, {
        resolution,
        reason: reason || undefined,
      });
      onResolved();
    } catch {
      // api-client handles 401; surface other failures so the operator isn't
      // left wondering whether the resolve actually landed.
      toast.error("Action failed");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete(rescan: { note?: string } | undefined) {
    setIsSubmitting(true);
    try {
      await api.post(`/documents/${document.id}/delete`, rescan ? { rescan } : {});
      onResolved();
    } catch {
      toast.error("Delete failed");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="esc-panel" onClick={onClose}>
      <div
        className="esc-panel-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="esc-panel-header">
          <span className="esc-panel-title">
            Escalation: {document.filename}
          </span>
          <button className="esc-panel-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="esc-panel-body">
          {ctx?.ruleName && (
            <div className="esc-detail-section">
              <div className="esc-detail-label">Rule</div>
              <div className="esc-detail-value">{ctx.ruleName}</div>
            </div>
          )}

          {ctx?.reason && (
            <div className="esc-detail-section">
              <div className="esc-detail-label">Reason</div>
              <div className="esc-detail-value">{ctx.reason}</div>
            </div>
          )}

          {isMisidentified && (
            <div className="esc-detail-section">
              <div className="esc-detail-label">Flag</div>
              <div className="esc-detail-value">Wrong document type</div>
            </div>
          )}

          {ctx?.operatorInput && (
            <div className="esc-detail-section">
              <div className="esc-detail-label">Operator Input</div>
              <div className="esc-detail-value esc-detail-mono">
                {ctx.operatorInput}
              </div>
            </div>
          )}

          {ctx?.ocrValue && (
            <div className="esc-detail-section">
              <div className="esc-detail-label">OCR Value</div>
              <div className="esc-detail-value esc-detail-mono">
                {ctx.ocrValue}
              </div>
            </div>
          )}

          {ctx?.triggeredBy && (
            <div className="esc-detail-section">
              <div className="esc-detail-label">Escalated By</div>
              <div className="esc-detail-value">{ctx.triggeredBy}</div>
            </div>
          )}

          <div className="esc-detail-section">
            <div className="esc-detail-label">Resolution Note (optional)</div>
            <input
              className="esc-reason-input"
              type="text"
              placeholder="Add a reason for the resolution..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <div className="esc-panel-actions">
          <button
            className="esc-btn esc-btn--accept"
            onClick={() => handleResolve("accept")}
            disabled={isSubmitting}
          >
            Accept Value
          </button>
          <button
            className="esc-btn esc-btn--override"
            onClick={() => handleResolve("override")}
            disabled={isSubmitting}
          >
            Override
          </button>
          <button
            className="esc-btn esc-btn--reject"
            onClick={() => handleResolve("reject")}
            disabled={isSubmitting}
          >
            Reject Document
          </button>
          {showReclassify && (
            <button
              className="esc-btn esc-btn--secondary"
              onClick={handleReclassifyMisfire}
              disabled={isSubmitting}
            >
              Reclassify (mis-identified)
            </button>
          )}
          {isMisidentified && canDelete && (
            <button
              className="esc-btn esc-btn--reject"
              onClick={() => setShowDelete(true)}
              disabled={isSubmitting}
            >
              Delete + request rescan
            </button>
          )}
        </div>
        {showDelete && (
          <ConfirmDeleteDialog
            title="Delete document?"
            message="All pages and images are removed permanently. The document is voided with an audit record."
            busy={isSubmitting}
            onConfirm={(rescan) => void handleDelete(rescan)}
            onCancel={() => setShowDelete(false)}
          />
        )}
      </div>
    </div>
  );
}
